import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type {
  ConfiguredProductInput,
  CartProductConfiguration,
  ConfiguredComboComponentInput,
  PublicProduct,
  SelectedOptionGroupInput,
} from '../../shared/types';
import { cartLineId, MAX_CART_LINES, normalizeCartConfiguration } from '../../shared/cart';
import { useTenant } from './TenantContext';

export { MAX_CART_LINES } from '../../shared/cart';
export type { CartProductConfiguration } from '../../shared/types';

export interface CartLine {
  id: string;
  product: PublicProduct;
  quantity: number;
  configuration: CartProductConfiguration;
  unitPriceUsdCents: number;
}

interface CartContextValue {
  lines: CartLine[];
  itemCount: number;
  totalUsdCents: number;
  add(
    product: PublicProduct,
    quantity: number,
    configuration: CartProductConfiguration,
    unitPriceUsdCents: number,
    replaceLineId?: string,
  ): boolean;
  setQuantity(lineId: string, quantity: number, unitPriceUsdCents?: number): void;
  remove(lineId: string): void;
  clear(): void;
}

interface StoredCartLine {
  productId: string;
  quantity: number;
  configuration: CartProductConfiguration;
}

const STORAGE_KEY = 'menucloud.cart';
const TENANT_STORAGE_KEY = 'menucloud.cart.tenant';
const STORAGE_VERSION = 2;
const CartContext = createContext<CartContextValue | null>(null);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseGroups(value: unknown): SelectedOptionGroupInput[] | null {
  if (!Array.isArray(value)) return null;
  const groups: SelectedOptionGroupInput[] = [];
  for (const candidate of value) {
    if (!isRecord(candidate) || typeof candidate.groupId !== 'string' || !Array.isArray(candidate.options)) return null;
    const options: SelectedOptionGroupInput['options'] = [];
    for (const option of candidate.options) {
      if (!isRecord(option) || typeof option.optionId !== 'string' || !Number.isInteger(option.quantity) || Number(option.quantity) < 1) return null;
      options.push({ optionId: option.optionId, quantity: Number(option.quantity) });
    }
    groups.push({ groupId: candidate.groupId, options });
  }
  return groups;
}

function parseConfiguredProduct(value: unknown): ConfiguredProductInput | null {
  if (!isRecord(value) || typeof value.productId !== 'string') return null;
  if (value.variantId !== undefined && value.variantId !== null && typeof value.variantId !== 'string') return null;
  const groups = parseGroups(value.groups);
  if (!groups || !Array.isArray(value.excludedIngredientIds) || !value.excludedIngredientIds.every((id) => typeof id === 'string')) return null;
  const includedIngredientIds = value.includedIngredientIds === undefined ? [] : value.includedIngredientIds;
  if (!Array.isArray(includedIngredientIds) || !includedIngredientIds.every((id) => typeof id === 'string')) return null;
  return {
    productId: value.productId,
    variantId: typeof value.variantId === 'string' ? value.variantId : null,
    groups,
    excludedIngredientIds: [...value.excludedIngredientIds],
    includedIngredientIds: [...includedIngredientIds],
  };
}

function parseConfiguration(value: unknown): CartProductConfiguration | null {
  const base = parseConfiguredProduct(value);
  if (!base || !isRecord(value) || !Array.isArray(value.comboComponents)) return null;
  const comboComponents: ConfiguredComboComponentInput[] = [];
  for (const candidate of value.comboComponents) {
    if (!isRecord(candidate) || typeof candidate.componentId !== 'string' || !Array.isArray(candidate.units)) return null;
    const units: ConfiguredProductInput[] = [];
    for (const unit of candidate.units) {
      const parsed = parseConfiguredProduct(unit);
      if (!parsed) return null;
      units.push(parsed);
    }
    comboComponents.push({ componentId: candidate.componentId, units });
  }
  return { ...base, comboComponents };
}

function effectivePrice(base: number, product: PublicProduct): number {
  if (product.promoType === 'percentage') return Math.max(0, Math.round(base * (1 - product.promoValue / 100)));
  if (product.promoType === 'fixed') return Math.max(0, Math.round(product.promoValue));
  return base;
}

function selectedOptionsPrice(product: PublicProduct, configuration: ConfiguredProductInput): number {
  const variantId = configuration.variantId ?? null;
  const selected = new Map(configuration.groups.map((group) => [group.groupId, group.options]));
  return product.optionGroups
    .filter((group) => group.variantIds.length === 0 || (variantId !== null && group.variantIds.includes(variantId)))
    .reduce((total, group) => {
      const quantities = new Map((selected.get(group.id) ?? []).map((option) => [option.optionId, option.quantity]));
      return total + group.options.reduce((sum, option) => sum + option.extraPriceUsdCents * (quantities.get(option.id) ?? 0), 0);
    }, 0);
}

function ingredientsPrice(product: PublicProduct, configuration: ConfiguredProductInput): number {
  const excluded = new Set(configuration.excludedIngredientIds);
  const included = new Set(configuration.includedIngredientIds);
  return product.ingredients.reduce((total, ingredient) => (
    (ingredient.includedByDefault ? !excluded.has(ingredient.id) : included.has(ingredient.id))
      ? total + ingredient.extraPriceUsdCents
      : total
  ), 0);
}

export function configuredUnitPrice(
  product: PublicProduct,
  configuration: CartProductConfiguration,
  products: ReadonlyMap<string, PublicProduct>,
): number {
  const variant = product.variants.find((item) => item.id === configuration.variantId);
  const catalogBase = product.priceMode === 'variante' ? (variant?.priceUsdCents ?? 0) : product.priceUsdCents;
  let total = effectivePrice(catalogBase, product) + ingredientsPrice(product, configuration) + selectedOptionsPrice(product, configuration);
  for (const configuredComponent of configuration.comboComponents) {
    const component = product.comboComponents.find((item) => item.id === configuredComponent.componentId);
    if (!component) continue;
    for (const unit of configuredComponent.units) {
      const choice = component.products.find((item) => item.productId === unit.productId);
      const componentProduct = products.get(unit.productId);
      if (!choice || !componentProduct) continue;
      total += choice.extraPriceUsdCents + ingredientsPrice(componentProduct, unit) + selectedOptionsPrice(componentProduct, unit);
    }
  }
  return total;
}

function basicConfigurationIssue(
  product: PublicProduct,
  configuration: ConfiguredProductInput,
  branchId: string,
): string | null {
  if (configuration.productId !== product.id || (branchId && product.unavailableBranchIds.includes(branchId))) {
    return `${product.name} no está disponible en esta sucursal.`;
  }
  const variantId = configuration.variantId ?? null;
  if (product.priceMode === 'variante') {
    if (!variantId) return `Selecciona una variante para ${product.name}.`;
    const variant = product.variants.find((item) => item.id === variantId);
    if (!variant || (branchId && variant.unavailableBranchIds.includes(branchId))) return `La variante de ${product.name} no está disponible.`;
  } else if (variantId) {
    return `${product.name} no utiliza variantes.`;
  }

  const excluded = new Set(configuration.excludedIngredientIds);
  const included = new Set(configuration.includedIngredientIds);
  if (excluded.size !== configuration.excludedIngredientIds.length) return `Hay ingredientes duplicados en ${product.name}.`;
  if (included.size !== configuration.includedIngredientIds.length) return `Hay ingredientes duplicados en ${product.name}.`;
  if ([...included].some((id) => excluded.has(id))) return `Un ingrediente de ${product.name} está incluido y excluido a la vez.`;
  for (const ingredientId of excluded) {
    const ingredient = product.ingredients.find((item) => item.id === ingredientId);
    if (!ingredient || !ingredient.selectable || !ingredient.includedByDefault) return `Una exclusión de ${product.name} no es válida.`;
  }
  for (const ingredientId of included) {
    const ingredient = product.ingredients.find((item) => item.id === ingredientId);
    if (!ingredient || !ingredient.selectable || ingredient.includedByDefault) return `Un ingrediente adicional de ${product.name} no es válido.`;
  }

  const applicableGroups = product.optionGroups.filter((group) => (
    group.variantIds.length === 0 || (variantId !== null && group.variantIds.includes(variantId))
  ));
  const configuredGroups = new Map(configuration.groups.map((group) => [group.groupId, group.options]));
  if (configuredGroups.size !== configuration.groups.length) return `Hay grupos duplicados en ${product.name}.`;
  if (configuration.groups.some((group) => !applicableGroups.some((item) => item.id === group.groupId))) return `Una opción de ${product.name} no corresponde a la variante.`;
  for (const group of applicableGroups) {
    const options = configuredGroups.get(group.id) ?? [];
    const uniqueOptions = new Set(options.map((option) => option.optionId));
    if (uniqueOptions.size !== options.length) return `Hay opciones duplicadas en ${group.name}.`;
    let count = 0;
    for (const selection of options) {
      const option = group.options.find((item) => item.id === selection.optionId);
      if (!option || (branchId && option.unavailableBranchIds.includes(branchId))) return `Una opción de ${group.name} no está disponible.`;
      if (!Number.isInteger(selection.quantity) || selection.quantity < 1 || (group.selectionType !== 'quantity' && selection.quantity !== 1)) {
        return `La cantidad seleccionada en ${group.name} no es válida.`;
      }
      count += group.selectionType === 'quantity' ? selection.quantity : 1;
    }
    const min = Math.max(group.required ? 1 : 0, group.minSelections);
    if (count < min || count > group.maxSelections || (group.selectionType === 'single' && count > 1)) {
      return `Selecciona entre ${min} y ${group.maxSelections} opción(es) en ${group.name}.`;
    }
  }
  return null;
}

export function configurationIssue(
  product: PublicProduct,
  configuration: CartProductConfiguration,
  products: ReadonlyMap<string, PublicProduct>,
  branchId: string,
): string | null {
  const basicIssue = basicConfigurationIssue(product, configuration, branchId);
  if (basicIssue) return basicIssue;
  if (product.type !== 'combo') return configuration.comboComponents.length ? 'Esta configuración de combo no es válida.' : null;
  const configuredComponents = new Map(configuration.comboComponents.map((component) => [component.componentId, component]));
  if (configuredComponents.size !== configuration.comboComponents.length || configuredComponents.size !== product.comboComponents.length) {
    return `Completa todos los componentes de ${product.name}.`;
  }
  for (const component of product.comboComponents) {
    const configured = configuredComponents.get(component.id);
    if (!configured || configured.units.length !== component.quantity) return `${component.name} requiere ${component.quantity} unidad(es).`;
    for (const unit of configured.units) {
      if (!component.products.some((choice) => choice.productId === unit.productId)) return `Una selección de ${component.name} no es válida.`;
      const componentProduct = products.get(unit.productId);
      if (!componentProduct || componentProduct.type === 'combo') return `Un producto de ${component.name} no está disponible.`;
      const issue = basicConfigurationIssue(componentProduct, unit, branchId);
      if (issue) return issue;
    }
  }
  return null;
}

function readStoredLines(): StoredCartLine[] {
  try {
    const parsed: unknown = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
    if (!isRecord(parsed) || parsed.version !== STORAGE_VERSION || !Array.isArray(parsed.lines)) return [];
    const lines: StoredCartLine[] = [];
    for (const candidate of parsed.lines) {
      if (!isRecord(candidate) || typeof candidate.productId !== 'string' || !Number.isInteger(candidate.quantity)) return [];
      const quantity = Number(candidate.quantity);
      const configuration = parseConfiguration(candidate.configuration);
      if (!configuration || quantity < 1 || quantity > 20 || configuration.productId !== candidate.productId) return [];
      lines.push({ productId: candidate.productId, quantity, configuration: normalizeCartConfiguration(configuration) });
    }
    return lines;
  } catch {
    return [];
  }
}

function writeStoredLines(lines: CartLine[]): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
      version: STORAGE_VERSION,
      lines: lines.map((line) => ({ productId: line.product.id, quantity: line.quantity, configuration: line.configuration })),
    }));
  } catch {
    // El carrito sigue funcionando aunque el navegador bloquee sessionStorage.
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const { tenant } = useTenant();
  const [lines, setLines] = useState<CartLine[]>([]);
  const persist = useCallback((updater: (current: CartLine[]) => CartLine[]) => {
    setLines((current) => {
      const next = updater(current);
      writeStoredLines(next);
      return next;
    });
  }, []);
  const add = useCallback((
    product: PublicProduct,
    quantity: number,
    configuration: CartProductConfiguration,
    unitPriceUsdCents: number,
    replaceLineId?: string,
  ) => {
    const normalized = normalizeCartConfiguration(configuration);
    const id = cartLineId(normalized);
    const remaining = replaceLineId ? lines.filter((line) => line.id !== replaceLineId) : lines;
    if (!remaining.some((line) => line.id === id) && remaining.length >= MAX_CART_LINES) return false;
    persist((current) => {
      const withoutReplaced = replaceLineId ? current.filter((line) => line.id !== replaceLineId) : current;
      const existing = withoutReplaced.find((line) => line.id === id);
      if (existing) {
        return withoutReplaced.map((line) => line.id === id
          ? { ...line, product, configuration: normalized, unitPriceUsdCents, quantity: Math.min(20, line.quantity + quantity) }
          : line);
      }
      if (withoutReplaced.length >= MAX_CART_LINES) return current;
      return [...withoutReplaced, { id, product, quantity: Math.min(20, Math.max(1, quantity)), configuration: normalized, unitPriceUsdCents }];
    });
    return true;
  }, [lines, persist]);
  const setQuantity = useCallback((id: string, quantity: number, unitPriceUsdCents?: number) => {
    persist((current) => quantity <= 0
      ? current.filter((line) => line.id !== id)
      : current.map((line) => line.id === id
        ? { ...line, quantity: Math.min(20, quantity), unitPriceUsdCents: unitPriceUsdCents ?? line.unitPriceUsdCents }
        : line));
  }, [persist]);
  const remove = useCallback((id: string) => persist((current) => current.filter((line) => line.id !== id)), [persist]);
  const clear = useCallback(() => persist(() => []), [persist]);

  useEffect(() => {
    if (!tenant) return;
    let previousTenant: string | null = null;
    try {
      previousTenant = sessionStorage.getItem(TENANT_STORAGE_KEY);
      sessionStorage.setItem(TENANT_STORAGE_KEY, tenant.id);
    } catch {
      // La ausencia de almacenamiento no impide iniciar un carrito nuevo.
    }
    if (previousTenant !== tenant.id) {
      setLines([]);
      writeStoredLines([]);
      return;
    }

    const products = new Map(tenant.categories.flatMap((category) => category.products).map((product) => [product.id, product]));
    const merged = new Map<string, CartLine>();
    for (const stored of readStoredLines()) {
      const product = products.get(stored.productId);
      if (!product || configurationIssue(product, stored.configuration, products, '')) continue;
      const id = cartLineId(stored.configuration);
      const existing = merged.get(id);
      const quantity = Math.min(20, stored.quantity + (existing?.quantity ?? 0));
      merged.set(id, {
        id,
        product,
        quantity,
        configuration: stored.configuration,
        unitPriceUsdCents: configuredUnitPrice(product, stored.configuration, products),
      });
    }
    const restored = [...merged.values()];
    setLines(restored);
    writeStoredLines(restored);
  }, [tenant]);

  const itemCount = useMemo(() => lines.reduce((sum, line) => sum + line.quantity, 0), [lines]);
  const totalUsdCents = useMemo(() => lines.reduce((sum, line) => sum + line.unitPriceUsdCents * line.quantity, 0), [lines]);
  const value = useMemo(() => ({ lines, itemCount, totalUsdCents, add, setQuantity, remove, clear }), [lines, itemCount, totalUsdCents, add, setQuantity, remove, clear]);
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const value = useContext(CartContext);
  if (!value) throw new Error('useCart debe usarse dentro de CartProvider.');
  return value;
}
